import { useState, useCallback } from 'react';
import { type NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { useEventReactions } from '@/hooks/useEventReactions';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { NoteContent } from '@/components/NoteContent';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';
import { Heart, MessageCircle, Loader2 } from 'lucide-react';

function formatRelativeTime(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;

  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

interface NoteCardProps {
  event: NostrEvent;
  className?: string;
  onReply?: (event: NostrEvent) => void;
}

export function NoteCard({ event, className, onReply }: NoteCardProps) {
  const author = useAuthor(event.pubkey);
  const { data: interactions } = useEventReactions(event.id, event.pubkey);
  const { user } = useCurrentUser();
  const { mutate: publish, isPending: isLiking } = useNostrPublish();
  const { toast } = useToast();

  const [optimisticallyLiked, setOptimisticallyLiked] = useState(false);
  const [optimisticLikeCount, setOptimisticLikeCount] = useState(0);

  const metadata = author.data?.metadata;
  const displayName = metadata?.display_name || metadata?.name || 'Anon';
  const profileImage = metadata?.picture;
  const nip05 = metadata?.nip05;

  const reactions = interactions?.reactions;
  const replies = interactions?.replies;

  const liked = optimisticallyLiked || (reactions?.liked ?? false);
  const likeCount = (reactions?.likes ?? 0) + optimisticLikeCount;
  const replyCount = replies?.count ?? 0;

  // Try to encode as npub for the link
  let profileLink = '#';
  try {
    profileLink = `/${nip19.npubEncode(event.pubkey)}`;
  } catch {
    // fallback
  }

  const initials = displayName.slice(0, 2).toUpperCase();

  const handleLike = useCallback(() => {
    if (!user) {
      toast({ description: 'Log in to like posts.' });
      return;
    }

    if (liked) return; // Already liked, no un-like in NIP-25

    // Optimistic update
    setOptimisticallyLiked(true);
    setOptimisticLikeCount(1);

    publish(
      {
        kind: 7,
        content: '+',
        tags: [
          ['e', event.id],
          ['p', event.pubkey],
          ['k', '1'],
        ],
      },
      {
        onError: () => {
          setOptimisticallyLiked(false);
          setOptimisticLikeCount(0);
          toast({ description: 'Failed to like. Please try again.' });
        },
      },
    );
  }, [user, liked, event, publish, toast]);

  return (
    <Card className={cn('border-border/50 hover:border-border transition-colors', className)}>
      <CardContent className="p-4">
        <div className="flex gap-3">
          {/* Avatar */}
          <a href={profileLink} className="shrink-0">
            <Avatar className="h-10 w-10 rounded-full">
              <AvatarImage src={profileImage} alt={displayName} />
              <AvatarFallback className="bg-bitcoin/10 text-bitcoin text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
          </a>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header row */}
            <div className="flex items-baseline gap-2 flex-wrap">
              <a
                href={profileLink}
                className="font-semibold text-foreground hover:underline truncate max-w-[200px]"
              >
                {displayName}
              </a>
              {nip05 && (
                <span className="text-xs text-muted-foreground truncate">
                  @{nip05}
                </span>
              )}
              <span className="text-xs text-muted-foreground shrink-0">
                {formatRelativeTime(event.created_at)}
              </span>
            </div>

            {/* Note content */}
            <div className="text-sm text-foreground leading-relaxed break-words mt-1">
              <NoteContent event={event} />
            </div>

            {/* Action bar */}
            <div className="flex items-center gap-1 mt-3 -ml-2">
              {/* Reply button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-muted-foreground hover:text-foreground hover:bg-accent/50 px-2.5"
                onClick={() => onReply?.(event)}
                title="Reply"
              >
                <MessageCircle className="h-4 w-4" />
                {replyCount > 0 && (
                  <span className="text-xs font-medium">{replyCount}</span>
                )}
              </Button>

              {/* Like button */}
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 gap-1.5 px-2.5 transition-colors',
                  liked
                    ? 'text-red-500 hover:text-red-500 hover:bg-red-500/10'
                    : 'text-muted-foreground hover:text-red-500 hover:bg-accent/50',
                )}
                onClick={handleLike}
                disabled={isLiking || liked}
                title={liked ? 'Liked' : 'Like'}
              >
                {isLiking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Heart className={cn('h-4 w-4', liked && 'fill-current')} />
                )}
                {likeCount > 0 && (
                  <span className="text-xs font-medium">{likeCount}</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
